import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Tabs, Tab, TextField, IconButton, Tooltip, Snackbar, Alert, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon, TrendingUp as IncomeIcon, TrendingDown as ExpenseIcon, AccountBalanceWallet as BalanceIcon, MoneyOff as DuesIcon,
} from '@mui/icons-material';
import { accountingAPI, academicAPI } from '../services/api';
import { money, errorText } from '../utils/format';
import { localDate, monthStart, monthEnd, addDays } from '../utils/dates';
import { StatCard, TabPanel } from './accounting/common';
import OverviewTab from './accounting/OverviewTab';
import DayBookTab from './accounting/DayBookTab';
import IncomeTab from './accounting/IncomeTab';
import ExpensesTab from './accounting/ExpensesTab';
import PayrollTab from './accounting/PayrollTab';
import BudgetsTab from './accounting/BudgetsTab';
import FeeDuesTab from './accounting/FeeDuesTab';
import ReportsTab from './accounting/ReportsTab';
import CategoriesTab from './accounting/CategoriesTab';
import VendorsTab from './accounting/VendorsTab';
import AssetsTab from './accounting/AssetsTab';

const TABS = ['Overview', 'Day book', 'Other income', 'Expenses', 'Payroll', 'Budgets', 'Fee dues', 'Vendors', 'Assets', 'Reports', 'Categories'];
const RANGE_TABS = [0, 1, 2, 3];

export default function AccountingManagement() {
  const [tab, setTab] = useState(0);
  const [range, setRange] = useState({ from: monthStart(), to: localDate() });
  const [preset, setPreset] = useState('month');
  const [year, setYear] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [refresh, setRefresh] = useState(0);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => setSnack({ open: true, message, severity }), []);
  const onChanged = useCallback(() => setRefresh((x) => x + 1), []);

  useEffect(() => { academicAPI.getActiveYear().then((y) => setYear(y || null)).catch(() => setYear(null)); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, t, ci, ce] = await Promise.all([
          accountingAPI.getSummary(range.from, range.to),
          accountingAPI.getTrend(12),
          accountingAPI.getCategories({ type: 'INCOME', activeOnly: true }),
          accountingAPI.getCategories({ type: 'EXPENSE', activeOnly: true }),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setTrend(t.data);
        setCategories({ income: ci.data.map((c) => c.name), expense: ce.data.map((c) => c.name) });
      } catch (e) { if (!cancelled) notify(errorText(e, 'Could not load accounting data'), 'error'); }
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to, refresh, notify]);

  const applyPreset = (p) => {
    if (!p) return;
    setPreset(p);
    const now = new Date();
    if (p === 'month') setRange({ from: monthStart(), to: localDate() });
    else if (p === 'lastMonth') { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); setRange({ from: monthStart(d), to: monthEnd(d) }); }
    else if (p === '30') setRange({ from: addDays(localDate(), -29), to: localDate() });
    else if (p === 'year') setRange({ from: year?.startDate || `${now.getFullYear()}-01-01`, to: localDate() });
  };

  const totalIncome = Number(summary?.totalIncome || 0);
  const totalOutflow = Number(summary?.totalOutflow || 0);
  const net = totalIncome - totalOutflow;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#004d40' }}>Accounting</Typography>
          <Typography variant="body2" color="text.secondary">Income, expenses, payroll, budgets and fee dues in one place</Typography>
        </Box>
        <Tooltip title="Refresh all figures"><IconButton onClick={onChanged} sx={{ bgcolor: '#e0f2f1', color: '#004d40' }}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total income" value={money(totalIncome)} icon={IncomeIcon} gradient="linear-gradient(135deg,#11998e,#38ef7d)"
            subtitle={`Fees ${money(summary?.feeIncome)} + other ${money(summary?.otherIncome)}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total outflow" value={money(totalOutflow)} icon={ExpenseIcon} gradient="linear-gradient(135deg,#ef6c00,#ffa726)"
            subtitle={`Expenses ${money(summary?.totalExpense)} + salaries ${money(summary?.salaryPaid)}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Net for the period" value={money(net)} icon={BalanceIcon}
            gradient={net >= 0 ? 'linear-gradient(135deg,#0288d1,#26c6da)' : 'linear-gradient(135deg,#eb3349,#f45c43)'} subtitle="Income minus outflow" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Fee dues" value={money(summary?.feeDue)} icon={DuesIcon} gradient="linear-gradient(135deg,#6a1b9a,#ab47bc)" subtitle="Outstanding from students" />
        </Grid>
      </Grid>

      {RANGE_TABS.includes(tab) && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={700} color="text.secondary">Period</Typography>
          <ToggleButtonGroup size="small" exclusive value={preset} onChange={(e, v) => applyPreset(v)}>
            <ToggleButton value="month">This month</ToggleButton>
            <ToggleButton value="lastMonth">Last month</ToggleButton>
            <ToggleButton value="30">Last 30 days</ToggleButton>
            <ToggleButton value="year">This academic year</ToggleButton>
          </ToggleButtonGroup>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={range.from}
            onChange={(e) => { setPreset(''); setRange((r) => ({ ...r, from: e.target.value })); }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={range.to}
            onChange={(e) => { setPreset(''); setRange((r) => ({ ...r, to: e.target.value })); }} />
        </Paper>
      )}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ bgcolor: '#f8f9fa', '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}>
          {TABS.map((t) => <Tab key={t} label={t} />)}
        </Tabs>
        <Box sx={{ p: 3, pt: 0 }}>
          <TabPanel value={tab} index={0}><OverviewTab summary={summary} trend={trend} /></TabPanel>
          <TabPanel value={tab} index={1}><DayBookTab range={range} notify={notify} /></TabPanel>
          <TabPanel value={tab} index={2}><IncomeTab range={range} categories={categories.income} notify={notify} onChanged={onChanged} /></TabPanel>
          <TabPanel value={tab} index={3}><ExpensesTab range={range} categories={categories.expense} notify={notify} onChanged={onChanged} /></TabPanel>
          <TabPanel value={tab} index={4}><PayrollTab notify={notify} onChanged={onChanged} /></TabPanel>
          <TabPanel value={tab} index={5}><BudgetsTab expenseCategories={categories.expense} notify={notify} /></TabPanel>
          <TabPanel value={tab} index={6}><FeeDuesTab notify={notify} /></TabPanel>
          <TabPanel value={tab} index={7}><VendorsTab notify={notify} /></TabPanel>
          <TabPanel value={tab} index={8}><AssetsTab notify={notify} /></TabPanel>
          <TabPanel value={tab} index={9}><ReportsTab notify={notify} /></TabPanel>
          <TabPanel value={tab} index={10}><CategoriesTab notify={notify} onChanged={onChanged} /></TabPanel>
        </Box>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
